"use client";

import { useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { AlertCircle, AlertTriangle, ArrowRight, Loader2, X } from "lucide-react";
import { ackWebhookAlerts, type ApiError, type WebhookAlert } from "../../../services/merchantApi";
import { formatCount } from "./format";
import { notifyWebhooksChanged, useWebhookSummary } from "./useWebhookSummary";
import { describeAlert } from "./vocab";

// ────────────────────────────────────────────────────────────────────────────
// WebhookAlertBanner — "your webhooks need attention", driven by the shared
// summary poller, so it never depends on an alert email having arrived.
//   variant="home" → Dashboard, above the Wallet card. Dismissible; the
//                    dismissal is remembered per alert STREAK (id + since),
//                    so a new failure streak shows the banner again.
//   variant="page" → the webhooks pages. Not dismissible.
// Renders nothing for non-merchants and when there are no alerts. Hosts and
// error messages originate from merchant input / their server and are only
// ever rendered as text.
// ────────────────────────────────────────────────────────────────────────────

const DISMISS_KEY = "cashtap:whBannerDismissed";

// One line per endpoint for the "several endpoints" form of the banner.
function oneLiner(a: WebhookAlert): string {
  if (a.kind === "endpoint_disabled") return `${a.urlHost} — disabled by CashTap`;
  if (a.kind === "deliveries_failed") {
    return `${a.urlHost} — ${formatCount(a.failedDeliveries)} ${
      a.failedDeliveries === 1 ? "delivery" : "deliveries"
    } failed permanently`;
  }
  return `${a.urlHost} — failing${a.lastError?.message ? `: ${a.lastError.message}` : ""}`;
}

// `id` is "{endpointId}:{kind}" and `since` is fixed for a whole streak, so
// the pair names one streak: a NEW streak yields a key we have never seen.
const streakKey = (a: WebhookAlert) => `${a.id}|${a.since}`;

// localStorage can throw (private mode, blocked storage) — the banner must
// still render; at worst a dismissal is not remembered.
function readDismissed(): string[] {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((k) => typeof k === "string") : [];
  } catch {
    return [];
  }
}

function writeDismissed(keys: string[]) {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(keys));
  } catch {
    // not persisted — it stays dismissed for this mount only
  }
}

/**
 * @param wallet  Redux wallet (null keeps the shared poller idle)
 * @param email   Redux email
 * @param variant "home" (dismissible, Dashboard) | "page" (webhooks pages)
 */
export function WebhookAlertBanner({
  wallet,
  email,
  variant,
}: {
  wallet: string | null;
  email: string | null;
  variant: "home" | "page";
}) {
  const { summary } = useWebhookSummary(wallet, email);
  const [dismissed, setDismissed] = useState<string[]>(() =>
    variant === "home" ? readDismissed() : [],
  );
  // Alerts acknowledged from this banner — hidden optimistically until the
  // refetched summary confirms it (or put back if the request fails).
  const [acked, setAcked] = useState<string[]>([]);
  const [acking, setAcking] = useState(false);

  if (!summary || !summary.merchant) return null;
  const alerts = summary.alerts.filter((a) => !acked.includes(streakKey(a)));
  if (alerts.length === 0) return null;
  if (variant === "home" && alerts.every((a) => dismissed.includes(streakKey(a)))) return null;

  const critical = alerts.some((a) => a.severity === "critical");
  const tone = critical
    ? {
        box: "border-red-500/30 bg-red-500/5",
        icon: "text-red-400",
        title: "text-red-200",
        body: "text-red-300/80",
        link: "text-red-200 hover:text-white",
        close: "text-red-300/70 hover:text-white",
      }
    : {
        box: "border-amber-500/30 bg-amber-500/5",
        icon: "text-amber-400",
        title: "text-amber-100",
        body: "text-amber-200/80",
        link: "text-amber-300 hover:text-white",
        close: "text-amber-300/70 hover:text-white",
      };
  const Icon = critical ? AlertCircle : AlertTriangle;

  const single = alerts.length === 1 ? alerts[0] : null;
  const copy = single ? describeAlert(single) : null;

  const dismiss = () => {
    // Replace, don't append: only streaks that are still active matter, so
    // the stored list can never grow without bound.
    const keys = alerts.map(streakKey);
    setDismissed(keys);
    writeDismissed(keys);
  };

  const markReviewed = async (a: WebhookAlert) => {
    if (acking) return;
    const key = streakKey(a);
    setAcking(true);
    setAcked((cur) => [...cur, key]);
    try {
      await ackWebhookAlerts(wallet, email, a.endpointId);
      notifyWebhooksChanged();
    } catch (e) {
      setAcked((cur) => cur.filter((k) => k !== key));
      toast.error((e as ApiError)?.message || "Couldn't mark as reviewed.");
    } finally {
      setAcking(false);
    }
  };

  return (
    <div
      role={critical ? "alert" : "status"}
      className={`mb-6 rounded-2xl border ${tone.box} p-4 sm:p-5 flex items-start gap-3`}
    >
      <Icon className={`w-5 h-5 ${tone.icon} shrink-0 mt-0.5`} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {single && copy ? (
          <>
            <p className={`${tone.title} font-semibold break-words`}>{copy.title}</p>
            <p className={`${tone.body} text-sm mt-1 break-words`}>{copy.body}</p>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
              <Link
                to={copy.ctaTo}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold ${tone.link}`}
              >
                {copy.ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              {copy.ackable && (
                <button
                  type="button"
                  onClick={() => markReviewed(single)}
                  disabled={acking}
                  className={`inline-flex items-center gap-1.5 text-xs ${tone.link} disabled:opacity-60`}
                >
                  {acking && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Mark as reviewed
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <p className={`${tone.title} font-semibold`}>
              {alerts.length} webhook endpoints need attention
            </p>
            <ul className={`${tone.body} text-sm mt-1 space-y-0.5`}>
              {alerts.slice(0, 3).map((a) => (
                <li key={a.id} className="break-words">
                  {oneLiner(a)}
                </li>
              ))}
              {alerts.length > 3 && <li>+{alerts.length - 3} more</li>}
            </ul>
            <div className="mt-3">
              <Link
                to="/developers/webhooks"
                className={`inline-flex items-center gap-1.5 text-xs font-semibold ${tone.link}`}
              >
                Open Webhooks <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </>
        )}
      </div>
      {variant === "home" && (
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className={`shrink-0 -mt-1.5 -mr-1.5 p-2.5 rounded-lg hover:bg-white/5 transition-colors ${tone.close}`}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
